import React, { useEffect, useState } from 'react';
import api from '../../libs/axios';
import { AddDepartmentOffcanvas } from './add';
import { EditDepartmentOffcanvas } from './edit';
import { DepartmentsList } from './list';
import {
  Department,
  DepartmentFormData,
  DepartmentHoliday,
  DepartmentPagination,
  DepartmentProject,
  EditDepartmentFormData
} from './types';
import { useDebounce } from '../../hooks/useDebounce';

export const DepartmentsManagement: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [projects, setProjects] = useState<DepartmentProject[]>([]);
  const [holidays, setHolidays] = useState<DepartmentHoliday[]>([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [pagination, setPagination] = useState<DepartmentPagination>({
    offset: 0,
    limit: 10,
    page: 1,
    total_pages: 1,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null
  });

  const [listLoading, setListLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' }>({
    field: 'name',
    direction: 'ASC'
  });

  const [showAddDepartment, setShowAddDepartment] = useState(false);
  const [showEditDepartment, setShowEditDepartment] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);

  const fetchDepartments = async (
    search: string = searchTerm,
    page: number = pagination.page,
    limitValue?: number,
    sort = sortConfig
  ) => {
    setListLoading(true);
    try {
      const response = await api.get('/departments/list', {
        params: {
          search: search.trim() || undefined,
          page,
          limit: limitValue || pagination.limit || 10,
          sort_by: sort.field,
          sort_order: sort.direction
        }
      });

      if (response.data.success) {
        const normalizedDepartments = (response.data.data.departments || []).map((department: any) => ({
          ...department,
          project_ids: Array.isArray(department.project_ids) ? department.project_ids : [],
          projects: Array.isArray(department.projects) ? department.projects : [],
          holiday_ids: Array.isArray(department.holiday_ids) ? department.holiday_ids : [],
          holidays: Array.isArray(department.holidays) ? department.holidays : []
        }));
        setDepartments(normalizedDepartments);
        setPagination((prev) => response.data.data.pagination || prev);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setListLoading(false);
    }
  };

  const fetchProjects = async () => {
    setProjectsLoading(true);
    try {
      const response = await api.get('/projects/list', {
        params: {
          limit: 100,
          status: 'ACTIVE'
        }
      });

      if (response.data.success) {
        setProjects(response.data.data.projects || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  // Active holidays of this year and next in the Holiday Calendar, for the
  // department's Holidays picker. Holidays a department is already linked to
  // still show from its own `holidays` (the picker's `linked` prop), even if
  // they fall outside this window.
  const fetchHolidays = async () => {
    setHolidaysLoading(true);
    try {
      const currentYear = new Date().getFullYear();
      const responses = await Promise.all(
        [currentYear, currentYear + 1].map((year) =>
          api.get('/calendar/holidays', {
            params: { year, is_active: true, limit: 1000, sort_by: 'holiday_date', sort_order: 'ASC' }
          })
        )
      );
      setHolidays(responses.flatMap((response) => response.data?.data?.holidays || []));
    } catch (error) {
      console.error('Error fetching holidays:', error);
    } finally {
      setHolidaysLoading(false);
    }
  };

  const createDepartment = async (data: DepartmentFormData) => {
    setActionLoading(true);
    try {
      const response = await api.post('/departments/create', data);
      if (response.data.success) {
        await fetchDepartments(searchTerm, 1);
        if (data.holiday_ids.length) fetchHolidays();
        setShowAddDepartment(false);
      }
    } catch (error) {
      console.error('Error creating department:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const updateDepartment = async (data: EditDepartmentFormData) => {
    setActionLoading(true);
    try {
      const response = await api.put(`/departments/${data.id}`, {
        name: data.name,
        project_ids: data.project_ids,
        holiday_ids: data.holiday_ids
      });
      if (response.data.success) {
        await fetchDepartments(searchTerm, pagination.page);
        // Linking can change which holidays apply to all departments.
        fetchHolidays();
        setShowEditDepartment(false);
        setSelectedDepartment(null);
      }
    } catch (error) {
      console.error('Error updating department:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    // API call triggered by debouncedSearch effect below
  };

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchDepartments(debouncedSearch, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleSort = (field: string, direction: 'ASC' | 'DESC') => {
    const sort = { field, direction };
    setSortConfig(sort);
    fetchDepartments(searchTerm, pagination.page, undefined, sort);
  };

  const handlePageChange = (page: number) => {
    fetchDepartments(searchTerm, page);
  };

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
    fetchDepartments(searchTerm, 1, limit);
  };

  const handleCloseModals = () => {
    setShowAddDepartment(false);
    setShowEditDepartment(false);
    setSelectedDepartment(null);
  };

  useEffect(() => {
    fetchDepartments();
    fetchProjects();
    fetchHolidays();
  }, []);

  return (
    <div>
      <DepartmentsList
        departments={departments}
        pagination={pagination}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onAddDepartment={() => setShowAddDepartment(true)}
        onEditDepartment={(department) => {
          setSelectedDepartment(department);
          setShowEditDepartment(true);
        }}
        onSort={handleSort}
        loading={listLoading}
        searchValue={searchTerm}
        sortState={sortConfig}
      />

      <AddDepartmentOffcanvas
        isOpen={showAddDepartment}
        onClose={handleCloseModals}
        onSubmit={createDepartment}
        loading={actionLoading}
        projects={projects}
        projectsLoading={projectsLoading}
        holidays={holidays}
        holidaysLoading={holidaysLoading}
      />

      <EditDepartmentOffcanvas
        isOpen={showEditDepartment}
        onClose={handleCloseModals}
        onSubmit={updateDepartment}
        department={selectedDepartment}
        loading={actionLoading}
        projects={projects}
        projectsLoading={projectsLoading}
        holidays={holidays}
        holidaysLoading={holidaysLoading}
      />
    </div>
  );
};

